import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import update from 'immutability-helper';
import { Modal, Button, Input, Tooltip, Select, Space } from 'antd';
import {
  aiCustomisationVisibilitySelector,
  aiConfigSelector,
  aiConfigCustomFieldSelector,
} from '@store/serviceIntegrations/integrationForm/selectors';
import {
  changeCustomisationModalVisibility,
  updateAiConfig,
} from '@store/serviceIntegrations/integrationForm/actions';
import {
  ModalContent,
  CustomFieldRow,
  CustomFieldsContainer,
  CustomFieldRemove,
  CustomFieldError,
  ModalLabel,
  TooltipMark,
  ErrorAndButtonContainer,
} from './styled';
import useContainerRef from '@src/hooks/utils/useContainerRef';

const severities = [
  {
    value: 'info',
    label: 'Info and above',
  },
  {
    value: 'warning',
    label: 'Warning and above',
  },
  {
    value: 'critical',
    label: 'Critical only',
  },
];

const AiAlertCustomisation = () => {
  const dispatch = useDispatch();

  const isVisible = useSelector(aiCustomisationVisibilitySelector);
  const aiConfig = useSelector(aiConfigSelector);
  const aiConfigCustomFields = useSelector(aiConfigCustomFieldSelector);

  const [selectedSeverity, setSelectedSeverity] = useState(aiConfig.severity || severities[0].value);
  const [customFields, setCustomFields] = useState(aiConfigCustomFields);
  const [emptyCustomFieldError, setEmptyCustomFieldError] = useState(false);

  const { containerRef, getContainer } = useContainerRef();

  useEffect(() => {
    setSelectedSeverity(aiConfig.severity || severities[0].value);
    setCustomFields(aiConfigCustomFields);
  }, [aiConfig.severity, aiConfigCustomFields]);

  const addCustomFieldRow = () => setCustomFields((prevCustomFields) => {
    const objKeys = Object.keys(prevCustomFields).sort((a, b) => a - b);
    const newKey = objKeys.length ? parseInt(objKeys[objKeys.length - 1], 10) + 1 : 0;
    return {
      ...prevCustomFields,
      [newKey]: {},
    };
  });

  const onKeyChange = (fieldKey, key) => {
    setCustomFields(prevCustomFields => update(prevCustomFields, {
      [fieldKey]: {
        key: {
          $set: key,
        },
      },
    }));
  };

  const onValueChange = (fieldKey, value) => {
    setCustomFields(prevCustomFields => update(prevCustomFields, {
      [fieldKey]: {
        value: {
          $set: value,
        },
      },
    }));
  };

  const onFieldRemove = (fieldKey) => {
    setCustomFields((prevCustomFields) => {
      const obj = { ...prevCustomFields };
      delete obj[fieldKey];
      return obj;
    });
  };

  const onCancel = () => {
    dispatch(changeCustomisationModalVisibility('ai'));
  };

  const onUpdate = () => {
    const isCustomFieldEmpty = Object.keys(customFields).some((fieldKey) => {
      const field = customFields[fieldKey];
      return !field.key || !field.value;
    });

    if (isCustomFieldEmpty) {
      setEmptyCustomFieldError(true);
    } else {
      setEmptyCustomFieldError(false);
      dispatch(updateAiConfig({
        severity: selectedSeverity,
        customFields: Object.values(customFields),
      }));
      dispatch(changeCustomisationModalVisibility('ai'));
    }
  };

  const footerButtons = [
    (
      <Button
        key="cancel"
        onClick={onCancel}
      >
        Cancel
      </Button>
    ),
    (
      <Button
        key="update"
        type="primary"
        onClick={onUpdate}
      >
        Update
      </Button>
    ),
  ];

  return (
    <Modal
      title="AI Alerts Settings"
      width="740px"
      open={isVisible}
      footer={footerButtons}
      onCancel={onCancel}
    >
      <ModalContent style={{ minHeight: 150 }} ref={containerRef}>

        <Space align='end' size={100}>
          <Space direction='vertical' size='small' >
            <ModalLabel>
              Severity
              <Tooltip title="Specify the AI alert severity level to assign to this alert." getPopupContainer={getContainer}>
                <TooltipMark />
              </Tooltip>
            </ModalLabel>

            <Select
              options={severities}
              value={selectedSeverity}
              onChange={setSelectedSeverity}
              style={{ width: 300 }}
            />
          </Space>

          <ErrorAndButtonContainer>
            <Button
              key="add"
              onClick={addCustomFieldRow}
            >
              Add Custom Field
            </Button>
            {
              emptyCustomFieldError
                ? <CustomFieldError>Empty values are not allowed</CustomFieldError>
                : null
            }
          </ErrorAndButtonContainer>
        </Space>

        <CustomFieldsContainer>
          {
            Object.keys(customFields).map((fieldKey) => {
              const field = customFields[fieldKey];

              return (
                <CustomFieldRow key={fieldKey}>
                  <Input
                    value={field.key}
                    placeholder="key"
                    // tslint:disable-next-line jsx-no-lambda
                    onChange={e => onKeyChange(fieldKey, e.target.value)}
                  />
                  <Input
                    value={field.value}
                    placeholder="value"
                    // tslint:disable-next-line jsx-no-lambda
                    onChange={e => onValueChange(fieldKey, e.target.value)}
                  />
                  {/* tslint:disable-next-line jsx-no-lambda */}
                  <CustomFieldRemove onClick={() => onFieldRemove(fieldKey)}>&times;</CustomFieldRemove>
                </CustomFieldRow>
              );
            })
          }
        </CustomFieldsContainer>
      </ModalContent>
    </Modal>
  );
};

export default AiAlertCustomisation;
